<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
    <!-- Lade-Zustand -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center min-h-[60vh]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E9041D] mb-4"></div>
      <p class="text-gray-600">Lade Daten...</p>
    </div>

    <!-- Fehler-Zustand -->
    <div v-else-if="error" class="flex flex-col items-center justify-center min-h-[60vh]">
      <div class="bg-red-50 p-4 rounded-lg mb-4">
        <p class="text-red-600">{{ error }}</p>
      </div>
      <button 
        @click="router.push('/dashboard')"
        class="px-4 py-2 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors"
      >
        Zurück zum Dashboard
      </button>
    </div>

    <!-- Hauptinhalt -->
    <div v-else-if="checkData" class="space-y-6">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6 bg-white rounded-xl p-5">
        <div>
          <h1 class="text-xl font-bold text-gray-800 mb-1">Check-In abschließen</h1>
          <p class="text-sm text-gray-600">Bitte erfasse die Kilometerstand und Tankstand.</p>
        </div>
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-[#E9041D] opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      <!-- Formular -->
      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-4">
          <form @submit.prevent="updateCheck">
            <div class="space-y-6">
              <!-- Kilometerstand -->
              <div>
                <label for="mileage" class="block text-sm font-medium text-gray-700 mb-1">Kilometerstand</label>
                <GermanDecimalInput
                  id="mileage"
                  v-model="formData.mileage"
                  :max-value="999999"
                  :max-digits-before-comma="6"
                  :max-digits-after-comma="0"
                  placeholder="z.B. 125000"
                  :unit="'km'"
                  :required="true"
                  aria-label="Kilometerstand"
                  input-class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4"
                />
              </div>

              <!-- Tankstand -->
              <div>
                <label for="fuelLevel" class="block text-sm font-medium text-gray-700 mb-1">Tankstand</label>
                <div class="space-y-2">
                  <!-- Elektrisches Fahrzeug -->
                  <template v-if="checkData.fuel_type === 'electric'">
                    <input 
                      type="range" 
                      id="fuelLevel" 
                      v-model="formData.fuelLevel" 
                      class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      min="0"
                      max="100"
                      step="1"
                    />
                    <div class="flex justify-between text-sm text-gray-600">
                      <span>0%</span>
                      <span>{{ formData.fuelLevel }}%</span>
                      <span>100%</span>
                    </div>
                  </template>
                  
                  <!-- Nicht-elektrisches Fahrzeug -->
                  <template v-else>
                    <select 
                      id="fuelLevel" 
                      v-model="formData.fuelLevel" 
                      class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                      required
                    >
                      <option value="0.125">1/8</option>
                      <option value="0.25">2/8</option>
                      <option value="0.375">3/8</option>
                      <option value="0.5">4/8</option>
                      <option value="0.625">5/8</option>
                      <option value="0.75">6/8</option>
                      <option value="0.875">7/8</option>
                      <option value="1">8/8</option>
                    </select>
                  </template>
                </div>
              </div>

              <!-- Buttons -->
              <div class="space-y-3">
                <button
                  type="submit"
                  :disabled="isSubmitting || !formData.mileage || Number(formData.mileage) <= 0"
                  class="w-full py-4 px-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Weiter
                </button>
                <button
                  type="button"
                  @click="showCancelDialog = true"
                  :disabled="isCancelling"
                  class="w-full py-3 px-4 bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Check-In abbrechen
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Cancel Confirmation Dialog -->
    <div v-if="showCancelDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
        <h3 class="text-lg font-bold text-gray-800 mb-2">Check-In abbrechen?</h3>
        <p class="text-sm text-gray-600 mb-6">Der Check-In wird unwiderruflich gelöscht. Alle bisherigen Eingaben gehen verloren.</p>
        <div class="flex space-x-3">
          <button
            @click="showCancelDialog = false"
            class="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Zurück
          </button>
          <button
            @click="cancelCheckin(checkinIdNumber)"
            :disabled="isCancelling"
            class="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            {{ isCancelling ? 'Wird gelöscht...' : 'Abbrechen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '../api/axios'
import GermanDecimalInput from '../components/GermanDecimalInput.vue'
import { useCheckinHeartbeat } from '../composables/useCheckinHeartbeat'
import { useCancelCheckin } from '../composables/useCancelCheckin'

const route = useRoute()
const router = useRouter()

const checkinIdNumber = computed(() => parseInt(route.params.id as string))

const {
  startHeartbeat,
  stopHeartbeat
} = useCheckinHeartbeat(checkinIdNumber)

const { isCancelling, showCancelDialog, cancelCheckin } = useCancelCheckin(stopHeartbeat)

// Zustände
const isLoading = ref(true)
const error = ref<string | null>(null)
const isSubmitting = ref(false)
const checkData = ref<any>(null)

// Formular-Daten
const formData = ref({
  mileage: '',
  fuelLevel: 100
})

// Lade Check-Daten
const fetchCheckData = async () => {
  isLoading.value = true
  error.value = null

  try {
    const response = await api.get(`/api/checker/checkins/${route.params.id}`)
    checkData.value = response.data.data

    // Pre-fill form with existing data (resume scenario)
    if (checkData.value.mileage != null) {
      formData.value.mileage = String(checkData.value.mileage)
    }
    if (checkData.value.fuel_level != null) {
      formData.value.fuelLevel = checkData.value.fuel_level
    }
  } catch (err: any) {
    console.error('Fehler beim Laden der Check-Daten:', err)
    error.value = 'Die Daten konnten nicht geladen werden. Bitte versuche es später erneut.'
  } finally {
    isLoading.value = false
  }
}

// Aktualisiere Check
const updateCheck = async () => {
  if (!checkData.value) return
  if (!formData.value.mileage || Number(formData.value.mileage) <= 0) {
    error.value = 'Bitte geben Sie den Kilometerstand ein.'
    return
  }

  isSubmitting.value = true

  try {
    await api.patch(`/api/checker/checkins/${route.params.id}`, {
      mileage: Number(formData.value.mileage),
      fuel_level: formData.value.fuelLevel
    })
    
    // Weiterleitung zur Schadensdokumentation
    router.push(`/checks/${route.params.id}/damage`)
  } catch (err: any) {
    console.error('Fehler beim Aktualisieren des Checks:', err)
    error.value = 'Der Check konnte nicht aktualisiert werden. Bitte versuche es erneut.'
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  fetchCheckData()
  if (checkinIdNumber.value) {
    startHeartbeat()
  }
})

onUnmounted(() => {
  stopHeartbeat()
})
</script> 